// Minimal async queue with fixed concurrency and global cancellation.
// We use this so dashboard refreshes happen in controllable batches instead of all at once.
// This is used for handling batch refreshes of components so its living in BaseObjects folder for now
type Task<T> = () => Promise<T>;

class AsyncQueue {
    private readonly concurrency: number;
    private running = 0;
    private cancelled = false;
    private cancelError: Error | null = null;
    private queue: Array<{
        task: Task<any>;
        resolve: (value: any) => void;
        reject: (reason?: any) => void;
    }> = [];

    constructor(maxConcurrency: number) {
        this.concurrency = maxConcurrency;
    }

    cancelAll(reason?: Error) {
        // Mark queue as cancelled, remember the error, and reject anything still waiting
        this.cancelled = true;
        const rejection = reason ?? new Error("Queue cancelled.");
        this.cancelError = rejection;

        while (this.queue.length) {
            const item = this.queue.shift();
            if (item) {
                item.reject(rejection);
            }
        }
    }

    enqueue<T>(task: Task<T>): Promise<T> {
        // If already cancelled, reject immediately so callers don't hang
        if (this.cancelled) {
            return Promise.reject(
                this.cancelError ?? new Error("Queue cancelled."),
            );
        }

        return new Promise<T>((resolve, reject) => {
            // Push task to the queue and kick the runner
            this.queue.push({ task, resolve, reject });
            this.runNext();
        });
    }

    private runNext() {
        // Only run if not cancelled and below concurrency limit
        if (this.cancelled) return;
        if (this.running >= this.concurrency) return;

        const item = this.queue.shift();
        if (!item) return;

        this.running++;

        item.task()
            .then(item.resolve)
            .catch(item.reject)
            .finally(() => {
                // When a task finishes, free a slot and start the next queued task
                this.running--;
                this.runNext();
            });
    }
}

export default AsyncQueue;
